/**
 * O contexto da sessão e o gancho que o lê, separados do provedor.
 *
 * A separação existe por uma razão prática: um arquivo que exporta componente **e** função
 * quebra a recarga a quente do Vite — ao editar o provedor, o React não consegue preservar
 * o estado e remonta a árvore inteira. Num cockpit isso significa perder a seleção e a
 * posição da fila a cada caractere digitado.
 */
import { createContext, useContext } from "react";

import type { Tema } from "../tema";
import type { Identidade } from "./tipos";

export interface EstadoDaSessao {
  quem: Identidade | null;
  /** `true` enquanto a renovação da partida não respondeu. */
  conferindo: boolean;
  entrar: (
    email: string,
    senha: string,
    organizacao?: string,
    codigo?: string,
  ) => Promise<void>;
  sair: () => Promise<void>;
  /** Por que a sessão acabou sem a pessoa pedir — a tela de entrada mostra (T-12). */
  avisoDeSaida: string | null;
  /** Troca o tema na hora e grava na conta (`C-19`). Se a API recusar, a tela volta ao
   *  tema anterior e a promessa rejeita — quem chamou diz que não deu. */
  trocarTema: (tema: Tema) => Promise<void>;
}

export const ContextoDaSessao = createContext<EstadoDaSessao | null>(null);

export function useSessao(): EstadoDaSessao {
  const contexto = useContext(ContextoDaSessao);
  if (!contexto)
    throw new Error("useSessao precisa de um ProvedorDeSessao acima.");
  return contexto;
}

/** O 409 do login: o e-mail atende a mais de uma organização e é preciso escolher. */
export class EscolhaDeOrganizacao extends Error {
  readonly organizacoes: { id: string; nome: string }[];

  constructor(organizacoes: { id: string; nome: string }[]) {
    super("Este e-mail atende a mais de uma organização. Escolha qual.");
    this.name = "EscolhaDeOrganizacao";
    this.organizacoes = organizacoes;
  }
}

/** O que a tela precisa para o cadastro do aplicativo autenticador (T-12). */
export interface CadastroDoSegundoFator {
  otpauth: string;
  segredo: string;
  qr_svg: string;
}

/**
 * O 401 do login com a senha certa: falta o código do aplicativo — ou falta cadastrá-lo, e
 * então vem junto o QR code. A tela pede o código e repete o login com senha **e** código.
 */
export class PedidoDeSegundoFator extends Error {
  readonly cadastro: CadastroDoSegundoFator | null;

  constructor(mensagem: string, cadastro: CadastroDoSegundoFator | null) {
    super(mensagem);
    this.name = "PedidoDeSegundoFator";
    this.cadastro = cadastro;
  }
}
